'use client';

import { CheckCircle, LinkSimple, PlugsConnected, WarningCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ConnectionStatus = 'connected' | 'pending' | 'needs_reconnect' | 'not_connected' | 'disabled';

export type ConnectionRequestOutput = {
  success?: boolean;
  display?: string;
  title?: string;
  message?: string;
  connections?: Array<{
    toolkitSlug?: string;
    appName?: string;
    reason?: string;
    required?: boolean;
    status?: ConnectionStatus | string;
    authUrl?: string | null;
  }>;
  continuePrompt?: string;
  skipPrompt?: string;
  error?: string;
};

function normalizeStatus(status: string | undefined): ConnectionStatus {
  if (status === 'connected') return 'connected';
  if (status === 'pending') return 'pending';
  if (status === 'needs_reconnect') return 'needs_reconnect';
  if (status === 'disabled') return 'disabled';
  return 'not_connected';
}

function statusLabel(status: ConnectionStatus) {
  if (status === 'connected') return 'Connected';
  if (status === 'pending') return 'Pending';
  if (status === 'needs_reconnect') return 'Reconnect';
  if (status === 'disabled') return 'Disabled';
  return 'Not connected';
}

function statusClass(status: ConnectionStatus) {
  if (status === 'connected') return 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200';
  if (status === 'pending') return 'border-amber-400/25 bg-amber-500/10 text-amber-200';
  if (status === 'needs_reconnect') return 'border-orange-400/25 bg-orange-500/10 text-orange-200';
  if (status === 'disabled') return 'border-zinc-400/20 bg-zinc-500/10 text-zinc-300';
  return 'border-white/10 bg-white/[0.04] text-white/55';
}

function StatusIcon({ status }: { status: ConnectionStatus }) {
  if (status === 'connected') return <CheckCircle weight="fill" className="size-3.5" />;
  if (status === 'disabled') return <XCircle weight="duotone" className="size-3.5" />;
  if (status === 'needs_reconnect') return <WarningCircle weight="duotone" className="size-3.5" />;
  return <LinkSimple weight="bold" className="size-3.5" />;
}

export function ConnectionRequestCard({
  output,
  onRespond,
}: {
  output: ConnectionRequestOutput;
  onRespond: (message: string) => void;
}) {
  const connections = (output.connections ?? []).filter((connection) => connection.toolkitSlug || connection.appName);
  const title = output.title || 'Connect apps to continue';
  const requiredCount = connections.filter((connection) => connection.required !== false).length;
  const connectedRequiredCount = connections.filter((connection) => (
    connection.required !== false && normalizeStatus(connection.status) === 'connected'
  )).length;
  const appList = connections.map((connection) => connection.appName || connection.toolkitSlug).filter(Boolean).join(', ');
  const continuePrompt = output.continuePrompt || `I have connected ${appList}. Continue with the previous task.`;
  const skipPrompt = output.skipPrompt || `Skip connecting ${appList} for now. Continue with what you can do without those accounts.`;

  if (output.success === false) {
    return (
      <div className="my-3 rounded-xl border border-orange-400/20 bg-orange-500/[0.06] p-4 text-sm text-orange-100/85">
        <div className="flex items-center gap-2 font-medium">
          <WarningCircle weight="duotone" className="size-4" />
          Could not prepare connection request
        </div>
        {output.error ? <p className="mt-2 text-xs leading-5 text-orange-100/70">{output.error}</p> : null}
      </div>
    );
  }

  return (
    <section className="my-3 overflow-hidden rounded-xl border border-white/[0.08] bg-[#121212]/95 shadow-lg">
      <div className="border-b border-white/[0.07] bg-gradient-to-r from-cyan-500/[0.12] via-violet-500/[0.08] to-transparent p-4">
        <div className="flex items-start gap-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg border border-cyan-300/20 bg-cyan-400/10 text-cyan-200">
            <PlugsConnected weight="duotone" className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            {output.message ? (
              <p className="mt-1 text-xs leading-5 text-white/55">{output.message}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="divide-y divide-white/[0.06]">
        {connections.map((connection) => {
          const status = normalizeStatus(connection.status);
          const name = connection.appName || connection.toolkitSlug || 'App';
          const canConnect = Boolean(connection.authUrl) && status !== 'connected' && status !== 'disabled';

          return (
            <div key={`${connection.toolkitSlug}-${name}`} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-white">{name}</p>
                  {connection.required === false ? (
                    <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/38">
                      Optional
                    </span>
                  ) : null}
                  <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]', statusClass(status))}>
                    <StatusIcon status={status} />
                    {statusLabel(status)}
                  </span>
                </div>
                {connection.reason ? (
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-white/45">{connection.reason}</p>
                ) : null}
              </div>

              {canConnect ? (
                <Button asChild size="sm" className="shrink-0">
                  <a href={connection.authUrl ?? '#'} target="_blank" rel="noreferrer">
                    Connect
                  </a>
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 border-t border-white/[0.07] bg-white/[0.025] p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-white/42">
          {requiredCount > 0
            ? `${connectedRequiredCount}/${requiredCount} required connected`
            : 'All listed connections are optional'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRespond(skipPrompt)}
            className="border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08] hover:text-white"
          >
            Skip
          </Button>
          <Button type="button" size="sm" onClick={() => onRespond(continuePrompt)}>
            Continue
          </Button>
        </div>
      </div>
    </section>
  );
}
